import { useFeatureAvailabilityContext } from '../services/featureAvailability/featureAvailabilityContext';
import { getApiKeyMessage } from '../services/featureAvailability/featureAvailabilityService';

/**
 * Custom hook to check if a feature is available
 * 
 * @param featureName - Name of the feature to check (e.g., 'populationAnalysis', 'simulations')
 * @returns Object with isAvailable boolean and optional reason string
 */
export function useFeatureAvailability(featureName: string): { isAvailable: boolean; reason?: string } {
  const { featureAvailability, isInitialized } = useFeatureAvailabilityContext();

  if (!isInitialized) {
    // Return unavailable until initialization is complete
    return { isAvailable: false, reason: 'Checking feature availability...' };
  }

  const feature = featureAvailability[featureName];

  if (!feature) {
    // Feature not found in availability matrix
    return { isAvailable: false, reason: `Feature '${featureName}' not found in availability matrix` };
  }

  if (feature.available) {
    return { isAvailable: true };
  }

  // Build user-friendly reason message
  const missingKeys = feature.requiredKeys || [];
  const reasons = missingKeys.map(key => getApiKeyMessage(key));
  const reason = reasons.length > 0 
    ? reasons.join(' ') 
    : feature.reason || 'This feature is not available';

  return {
    isAvailable: false,
    reason
  };
}

